import { create } from "zustand";
import axios from "axios";
const url = process.env.REACT_APP_API_BASE_URL;

/*
 * The dropdown sources (vendors/shapes/colors/cases) come from their own
 * endpoints, NOT from the products in hand - which is what makes paging
 * safe here. Had they been derived from the product list, paginating would
 * have quietly reduced every filter to the options present on page one.
 *
 * They also do not change between pages, so they are fetched once and the
 * paged product request goes out on its own after that.
 */
export const useProductStore = create((set, get) => ({
  products: [],
  vendors: [],
  shapes: [],
  colors: [],
  cases: [],
  loading: true,
  error: null,

  /* Server-side now. `total` is the whole catalogue; products.length is
     only the current page, so anything reporting a count has to read this
     instead. */
  page: 1,
  limit: 24,
  /* `total` is the filtered result count - what the pager reads.
     `catalogueTotal` is how many products the user owns, regardless of any
     search, which is what the summary card reports. Driving that card off
     the filtered count told someone with a full catalogue they had "0
     Products" the moment a search matched nothing. */
  total: 0,
  catalogueTotal: 0,
  totalPages: 1,
  optionsLoaded: false,

  /* Guards against an out-of-order response: a slow earlier request must
     not land after a newer one and repaint the grid with stale rows. */
  requestId: 0,

  fetchData: async (params = {}) => {
    const ticket = get().requestId + 1;
    set({ loading: true, error: null, requestId: ticket });
    const token = localStorage.getItem("userToken");
    try {
      const auth = { headers: { Authorization: `Bearer ${token}` } };
      const {
        page = 1,
        limit = get().limit,
        search = "",
        shape = "",
        vendor = "",
        case: caseType = "",
      } = params;

      const productRequest = axios.get(`${url}/auth/v1/myProducts`, {
        ...auth,
        params: {
          page,
          limit,
          ...(search ? { search } : {}),
          ...(shape ? { shape } : {}),
          ...(vendor ? { vendor } : {}),
          ...(caseType ? { case: caseType } : {}),
        },
      });

      /* Fetched once. They do not vary by page, so refetching them on every
         page turn is four requests for four unchanged answers. */
      const optionRequests = get().optionsLoaded
        ? []
        : [
            axios.get(`${url}/auth/v1/vendors`, auth),
            axios.get(`${url}/auth/v1/shapes`, auth),
            axios.get(`${url}/auth/v1/colors`, auth),
            axios.get(`${url}/auth/v1/cases`, auth),
          ];

      const [productResponse, ...optionResponses] = await Promise.all([
        productRequest,
        ...optionRequests,
      ]);

      if (get().requestId !== ticket) return;

      const [vendorResponse, shapeResponse, colorResponse, caseResponse] =
        optionResponses;

      const fetchedProducts = productResponse.data.products || [];
      const normalizedProducts = fetchedProducts.map((product) => ({
        ...product,
        _id: product._id || product.id || `temp-${Math.random()}`,
        name: product.name || "Unknown",
        height: product.height || 0,
        width: product.width || 0,
        vendor: product.vendor || "Unknown",
        createdDate: product.createdDate || new Date().toISOString(),
      }));

      const d = productResponse.data || {};

      set({
        products: normalizedProducts,
        page: d.page ?? page,
        limit: d.limit ?? limit,
        total: d.total ?? normalizedProducts.length,
        catalogueTotal: d.catalogueTotal ?? d.total ?? normalizedProducts.length,
        totalPages: d.totalPages ?? 1,
        loading: false,
        /* Only overwrite the option lists when this call actually fetched
           them; a page turn must not blank them out. */
        ...(optionResponses.length
          ? {
              vendors: vendorResponse.data.Vendors?.map((v) => v.name) || [],
              shapes: shapeResponse.data.Shapes?.map((s) => s.name) || [],
              colors: colorResponse.data.Colors?.map((c) => c.name) || [],
              cases: caseResponse.data.Cases?.map((c) => c.name) || [],
              optionsLoaded: true,
            }
          : {}),
      });
    } catch (err) {
      if (get().requestId !== ticket) return;
      set({
        error: "Failed to fetch data",
        loading: false,
      });
    }
  },
}));
